/**
 * TrendIndicator - Displays directional trend arrows with color-coded percentage changes
 *
 * @example
 * // Positive trend (green, up arrow)
 * <TrendIndicator current={150} previous={100} /> // Shows "▲ +50.0%"
 *
 * @example
 * // Negative trend (red, down arrow)
 * <TrendIndicator current={80} previous={100} /> // Shows "▼ -20.0%"
 *
 * @example
 * // Neutral/stable (gray, horizontal line)
 * <TrendIndicator current={100.05} previous={100} /> // Shows "─ 0.0%"
 *
 * @example
 * // Size variants
 * <TrendIndicator current={150} previous={100} size="sm" />
 * <TrendIndicator current={150} previous={100} size="md" />
 * <TrendIndicator current={150} previous={100} size="lg" />
 */

/** Threshold for "no significant change" (0.1%) */
const CHANGE_THRESHOLD = 0.1;

/** Props for the TrendIndicator component */
export interface TrendIndicatorProps {
  /** Current value to compare */
  current: number;
  /** Previous value to compare against (optional - if null/undefined, no indicator is shown) */
  previous?: number | null;
  /** Size variant for the indicator */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/** Trend direction type */
type TrendDirection = 'up' | 'down' | 'stable';

/** Size configuration for each variant */
const SIZE_CLASSES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

/** Arrow symbols for each trend direction */
const TREND_SYMBOLS: Record<TrendDirection, string> = {
  up: '▲',
  down: '▼',
  stable: '─',
};

/** Color classes for each trend direction */
const TREND_COLORS: Record<TrendDirection, string> = {
  up: 'text-success',
  down: 'text-error',
  stable: 'text-brand-gray',
};

/**
 * Calculate percentage change between current and previous values
 * @param current - Current value
 * @param previous - Previous value
 * @returns Percentage change or null if previous is null/undefined
 */
function calculateChange(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null;
  if (previous === 0) {
    // Handle division by zero
    return current > 0 ? 100 : current < 0 ? -100 : 0;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Determine trend direction based on percentage change
 * @param change - Percentage change value
 * @returns Trend direction: 'up', 'down', or 'stable'
 */
function getTrendDirection(change: number | null): TrendDirection {
  if (change === null || Math.abs(change) < CHANGE_THRESHOLD) return 'stable';
  return change > 0 ? 'up' : 'down';
}

/**
 * Generate accessible aria-label text for screen readers
 * @param direction - Trend direction
 * @param change - Percentage change value
 * @returns Descriptive text for screen readers
 */
function getAriaLabel(direction: TrendDirection, change: number | null): string {
  if (change === null || direction === 'stable') {
    return 'No significant change';
  }
  const absChange = Math.abs(change).toFixed(1);
  return direction === 'up'
    ? `Increased by ${absChange} percent`
    : `Decreased by ${absChange} percent`;
}

/**
 * Format the percentage change for display
 * @param direction - Trend direction
 * @param change - Percentage change value
 * @returns Formatted string with sign prefix
 */
function formatChange(direction: TrendDirection, change: number | null): string {
  if (change === null) return '0.0%';
  const absValue = Math.abs(change).toFixed(1);
  if (direction === 'up') return `+${absValue}%`;
  if (direction === 'down') return `-${absValue}%`;
  return `${absValue}%`;
}

/**
 * TrendIndicator component displays directional trend arrows with color-coded percentage changes
 */
export function TrendIndicator({
  current,
  previous,
  size = 'md',
  className = '',
}: TrendIndicatorProps): JSX.Element | null {
  // Don't render if previous value is not provided
  if (previous === null || previous === undefined) {
    return null;
  }

  const change = calculateChange(current, previous);
  const direction = getTrendDirection(change);
  const ariaLabel = getAriaLabel(direction, change);
  const formattedChange = formatChange(direction, change);
  const symbol = TREND_SYMBOLS[direction];
  const colorClass = TREND_COLORS[direction];
  const sizeClass = SIZE_CLASSES[size];

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={`
        inline-flex items-center gap-0.5 font-semibold font-body
        motion-safe:transition-colors motion-safe:duration-150
        ${sizeClass}
        ${colorClass}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      <span aria-hidden="true">{symbol}</span>
      <span>{formattedChange}</span>
    </span>
  );
}

export default TrendIndicator;
